import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Music, UploadCloud } from "lucide-react";
import { useProfile } from "@/context/ProfileContext";
import { StudioImage } from "@/components/ui-nw/StudioImage";
import { SectionLabel } from "@/components/ui-nw/UserAvatar";
import { PageLoader } from "@/components/auth/Guards";
import { TrackList } from "@/components/music/TrackCard";
import { GENRES } from "@/components/music/FormBits";
import { PAGES } from "@/constants/testIds";

const Banner = ({ isCreator }) => (
  <div className="relative overflow-hidden rounded-3xl border border-white/10 min-h-[240px] sm:min-h-[300px] flex items-end p-6 sm:p-10">
    <StudioImage name="producer" eager alt="Producer working a soundboard in a red-lit studio" sizes="(min-width: 1152px) 1152px, 100vw" className="absolute inset-0 h-full w-full object-cover" />
    <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(5,5,8,0.2)_0%,rgba(5,5,8,0.85)_65%,#050508_100%)] sm:bg-[linear-gradient(90deg,#050508_5%,rgba(5,5,8,0.75)_45%,rgba(5,5,8,0.15))]" />
    <div className="relative space-y-3 max-w-lg">
      <SectionLabel>Music</SectionLabel>
      <h1 className="font-display text-4xl sm:text-5xl font-extrabold tracking-tight chrome-text">AI Music Feed</h1>
      <p className="text-sm sm:text-base text-slate-300">Finished AI-created tracks, straight from the creators' studios.</p>
      {isCreator && <Link to="/upload" data-testid="music-upload-cta" className="btn-red inline-flex items-center gap-2 px-5 py-2.5 text-sm"><UploadCloud className="h-4 w-4" /> Upload a track</Link>}
    </div>
  </div>
);

export default function MusicPage() {
  const { request, profile } = useProfile();
  const [genre, setGenre] = useState("");
  const [tracks, setTracks] = useState(null);

  useEffect(() => {
    setTracks(null);
    request("get", "/tracks", null, { params: genre ? { genre } : {} }).then(setTracks).catch(() => setTracks([]));
  }, [genre, request]);

  return (
    <div data-testid={PAGES.music} className="space-y-6 animate-rise">
      <Banner isCreator={profile?.account_type === "creator"} />
      <div className="flex gap-2 overflow-x-auto -mx-4 px-4 pb-1 scrollbar-none">
        {["", ...GENRES].map((g) => (
          <button key={g || "all"} data-testid={`genre-filter-${(g || "all").toLowerCase().replace(/[^a-z0-9]/g, "")}`} onClick={() => setGenre(g)}
            className={`shrink-0 rounded-full px-4 py-1.5 text-xs font-semibold transition-colors ${genre === g ? "bg-nw-red text-white" : "border border-white/15 text-slate-300 hover:border-white/30"}`}>
            {g || "All"}
          </button>
        ))}
      </div>
      {tracks === null ? <PageLoader /> : (
        <TrackList tracks={tracks} empty={<span className="inline-flex items-center gap-2"><Music className="h-4 w-4" /> No tracks here yet.</span>} />
      )}
    </div>
  );
}
